import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { generatePublicUrl } from "../../../../urlConfig";

import {
    getOfficePaymentMethods,
    getOfficePaymentAccountsByOrder,
    createOfficePaymentAccount,
    deleteOfficePaymentAccount,
    saveOfficeOrderPaymentMethod,
    clearOfficePaymentAlert,
} from "../../../../actions/officePaymentAccount.actions";

import {
    formatDate,
    formatMoney,
    getId,
    getOfficeId,
    getOrderId,
    getPaymentState,
} from "./orderModalCenter.utils";
import { emitOfficeAnalyticsRefresh } from "../../../../utils/officeAnalyticsEvents";
import OfficeReceiptPreviewModal from "../../components/OfficeReceiptPreviewModal";

function formatActor(actor) {
    if (!actor) return "Неизвестный сотрудник";

    if (typeof actor === "string") return actor;

    const name = [actor.firstName, actor.lastName]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" ");

    return (
        actor.displayName ||
        name ||
        actor.username ||
        actor.email ||
        actor._id ||
        "Неизвестный сотрудник"
    );
}

function getPaymentKind(item) {
    return item?.type === "office" ? "office" : "client";
}

function getPaymentKindLabel(type) {
    return type === "office" ? "Расход офиса" : "Оплата клиента";
}

function getPaymentKindShortLabel(type) {
    return type === "office" ? "Офис" : "Клиент";
}

function getPaymentKindHint(type) {
    return type === "office"
        ? "Деньги, которые офис потратил по этому заказу."
        : "Деньги, которые клиент внёс по этому заказу.";
}

function getNumber(value) {
    const num = Number(value || 0);

    return Number.isFinite(num) ? num : 0;
}

function getEffectiveOrderAmount(order = {}) {
    const discountedTotal = getNumber(order?.discountForBuyers);
    const totalAmount = getNumber(order?.totalAmount);

    if (order.discountAmountConfirmed === true || discountedTotal > 0) return discountedTotal;

    return totalAmount;
}

function calculatePaymentTotals(paymentAccounts = [], totalAmountValue = 0, recordedAmount = null) {
    const totalAmount = getNumber(totalAmountValue);

    const receiptPaidAmount = paymentAccounts.reduce((sum, item) => {
        if (getPaymentKind(item) !== "client") return sum;

        return sum + getNumber(item?.price);
    }, 0);

    const clientPaidAmount = recordedAmount == null ? receiptPaidAmount : getNumber(recordedAmount);

    const officeExpenseAmount = paymentAccounts.reduce((sum, item) => {
        if (getPaymentKind(item) !== "office") return sum;

        return sum + getNumber(item?.price);
    }, 0);

    const remainingAmount = Math.max(0, totalAmount - clientPaidAmount);
    const overpaymentAmount = Math.max(0, clientPaidAmount - totalAmount);
    const netAmount = clientPaidAmount - officeExpenseAmount;

    return {
        totalAmount,
        clientPaidAmount,
        officeExpenseAmount,
        remainingAmount,
        overpaymentAmount,
        netAmount,
    };
}

function PaymentMethodCard({
    active,
    title,
    description,
    icon,
    onClick,
    operator,
    disabled,
}) {
    return (
        <button
            type="button"
            className={[
                "order-payment-method-AO",
                active ? "is-active" : "",
                operator ? "is-operator" : "",
                disabled ? "is-disabled" : "",
            ].filter(Boolean).join(" ")}
            onClick={onClick}
            disabled={disabled}
        >
            <span className="order-payment-method-AO__icon">
                {icon ? <img src={generatePublicUrl(icon)} alt={title} /> : "₽"}
            </span>

            <span className="order-payment-method-AO__body">
                <strong>{title}</strong>
                {!!description && <span>{description}</span>}
            </span>

            <span className="order-payment-method-AO__mark">
                {active ? "✓" : ""}
            </span>
        </button>
    );
}

function PaymentAccountItem({
    item,
    index,
    onDelete,
    onOpenReceipt,
    deleting,
}) {
    const files = Array.isArray(item?.itemPictures) ? item.itemPictures : [];
    const kind = getPaymentKind(item);

    return (
        <div className={[
            "order-payment-account-AO",
            `order-payment-account-AO--${kind}`,
        ].join(" ")}
        >
            <div className="order-payment-account-AO__index">
                {index + 1}
            </div>

            <div className="order-payment-account-AO__body">
                <div className="order-payment-account-AO__top">
                    <strong>{formatMoney(item?.price)}</strong>

                    <span className={[
                        "order-payment-account-AO__badge",
                        `order-payment-account-AO__badge--${kind}`,
                    ].join(" ")}
                    >
                        {getPaymentKindShortLabel(kind)}
                    </span>

                    <span className="order-payment-account-AO__type">
                        {getPaymentKindLabel(kind)} · {({ cash: "Наличные", bank_transfer: "Перевод", card: "Картой вне сайта" })[item?.method] || "Способ не указан"}
                    </span>
                </div>

                <div className="order-payment-account-AO__description">
                    {item?.description || "Без описания"}
                </div>

                <div className="order-payment-account-AO__date">
                    {formatDate(item?.date || item?.createdAt)}
                    {" · "}
                    Внёс: {formatActor(item?.createdBy)}
                </div>

                {!!files.length && (
                    <div className="order-payment-account-AO__files">
                        {files.map((file, fileIndex) => (
                            <button
                                key={file?._id || file?.receiptId || fileIndex}
                                type="button"
                                className="order-payment-receipt-AO"
                                onClick={() => onOpenReceipt?.(item, files, fileIndex)}
                            >
                                <span aria-hidden="true">▣</span>
                                <b>Чек {fileIndex + 1}</b>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <button
                type="button"
                className="btn"
                onClick={() => onDelete?.(item)}
                disabled={deleting}
            >
                {deleting ? "Удаляем..." : "Удалить"}
            </button>
        </div>
    );
}

function PaymentAccountGroup({
    title,
    hint,
    total,
    items,
    type,
    onDelete,
    onOpenReceipt,
    deletingId,
    deleteLoading,
}) {
    return (
        <div className={[
            "order-payment-group-AO",
            `order-payment-group-AO--${type}`,
        ].join(" ")}
        >
            <div className="order-payment-group-AO__head">
                <div>
                    <strong>{title}</strong>
                    <span>{hint}</span>
                </div>

                <b>{formatMoney(total)}</b>
            </div>

            {items.length ? (
                <div className="order-payment-group-AO__list">
                    {items.map((item, index) => (
                        <PaymentAccountItem
                            key={item._id || index}
                            item={item}
                            index={index}
                            onDelete={onDelete}
                            onOpenReceipt={onOpenReceipt}
                            deleting={deletingId === item._id || deleteLoading}
                        />
                    ))}
                </div>
            ) : (
                <div className="order-payment-empty-AO">
                    Записей пока нет.
                </div>
            )}
        </div>
    );
}

function OfficePaymentMethodSettings({
    paymentList = [],
    availablePaymentIds = [],
    setAvailablePaymentIds,
    onSave,
    saving,
    backendReady,
}) {
    const isEnabled = (id) => {
        if (!availablePaymentIds.length) return true;

        return availablePaymentIds.includes(String(id));
    };

    const toggle = (id) => {
        const safeId = String(id);

        setAvailablePaymentIds((prev) => {
            if (!prev.length) {
                return paymentList
                    .map((item) => String(item._id))
                    .filter((itemId) => itemId !== safeId);
            }

            if (prev.includes(safeId)) {
                return prev.filter((itemId) => itemId !== safeId);
            }

            return [...prev, safeId];
        });
    };

    return (
        <div className="order-payment-settings-AO">
            <div className="order-payment-settings-AO__note">
                <strong>Способы оплаты офиса</strong>
                <span>
                    Здесь офис выбирает, какие способы оплаты показывать в своих заказах.
                    Сейчас это локальная фронтовая настройка. Для сохранения на сервере
                    нужен backend endpoint `/office/payment-methods`.
                </span>
            </div>

            <div className="order-payment-settings-AO__list">
                {paymentList.length ? (
                    paymentList.map((payment) => (
                        <button
                            type="button"
                            key={payment._id}
                            className={[
                                "order-payment-settings-AO__item",
                                isEnabled(payment._id) ? "is-enabled" : "is-disabled",
                            ].filter(Boolean).join(" ")}
                            onClick={() => toggle(payment._id)}
                        >
                            <span className="order-payment-settings-AO__switch">
                                {isEnabled(payment._id) ? "✓" : ""}
                            </span>

                            <span className="order-payment-settings-AO__body">
                                <strong>{payment.title || "Способ оплаты"}</strong>
                                <span>{payment.description || "Без описания"}</span>
                            </span>
                        </button>
                    ))
                ) : (
                    <div className="order-payment-empty-AO">
                        Способы оплаты пока не загружены.
                    </div>
                )}
            </div>

            <div className="order-payment-settings-AO__footer">
                <div className={backendReady ? "is-ready" : "is-waiting"}>
                    {backendReady
                        ? "Настройки офиса получены с backend."
                        : "Backend-настройки офиса пока не подключены, используется общий paymentList."}
                </div>

                <button
                    type="button"
                    className="btn btn--primary"
                    onClick={onSave}
                    disabled={saving}
                >
                    {saving ? "Сохраняем..." : "Сохранить настройки офиса"}
                </button>
            </div>
        </div>
    );
}

function QuickAmountButton({
    label,
    value,
    onClick,
    disabled,
}) {
    return (
        <button
            type="button"
            className="order-payment-quick-AO__btn"
            onClick={() => onClick(value)}
            disabled={disabled || value <= 0}
        >
            <span>{label}</span>
            <b>{formatMoney(value)}</b>
        </button>
    );
}

export default function OrderPaymentEditor({
    orderDetails,
    setLocalError,
}) {
    const dispatch = useDispatch();

    const {
        paymentMethods = [],
        paymentMethodsSource = "",
        paymentAccounts = [],

        loadingMethods = false,
        loadingAccounts = false,
        createLoading = false,
        deleteLoading = false,
        saveMethodLoading = false,

        deletingId = "",
        error,
        message,
        showAlert,
    } = useSelector((state) => state.officePaymentAccount || {});

    const orderId = getOrderId(orderDetails);
    const officeId = getOfficeId(orderDetails);

    const [tab, setTab] = useState("method");
    const [paidSnapshot, setPaidSnapshot] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [availablePaymentIds, setAvailablePaymentIds] = useState([]);

    const [selectedPaymentId, setSelectedPaymentId] = useState("");
    const [paymentOrderOnOperator, setPaymentOrderOnOperator] = useState(false);

    const [form, setForm] = useState({
        price: "",
        description: "",
        type: "client",
        method: "cash",
    });

    const [itemPictures, setItemPictures] = useState([]);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [receiptPreview, setReceiptPreview] = useState(null);

    const handleOpenReceipt = (paymentAccount, receipts, index = 0) => {
        const paymentAccountId = String(paymentAccount?._id || "");

        if (!paymentAccountId || !Array.isArray(receipts) || !receipts.length) return;

        setReceiptPreview({
            paymentAccountId,
            receipts,
            initialIndex: index,
            title: getPaymentKind(paymentAccount) === "office"
                ? "Чек расхода офиса"
                : "Чек оплаты клиента",
        });
    };


    const loading = loadingMethods || loadingAccounts;

    const effectiveOrderAmount = useMemo(
        () => getEffectiveOrderAmount(orderDetails),
        [orderDetails?.discountForBuyers, orderDetails?.totalAmount]
    );

    const totals = useMemo(() => {
        return calculatePaymentTotals(paymentAccounts, effectiveOrderAmount, paidSnapshot?.orderId === orderId ? paidSnapshot.amount : orderDetails?.paidToOfficeAmount);
    }, [paymentAccounts, effectiveOrderAmount, paidSnapshot, orderId, orderDetails?.paidToOfficeAmount]);

    const paymentState = useMemo(() => {
        return getPaymentState(totals.totalAmount, totals.clientPaidAmount);
    }, [totals.totalAmount, totals.clientPaidAmount]);

    const clientPaymentAccounts = useMemo(() => {
        return paymentAccounts.filter((item) => getPaymentKind(item) === "client");
    }, [paymentAccounts]);

    const officePaymentAccounts = useMemo(() => {
        return paymentAccounts.filter((item) => getPaymentKind(item) === "office");
    }, [paymentAccounts]);

    const officePaymentSettingsReady = paymentMethodsSource === "office";

    const enabledPaymentList = useMemo(() => {
        if (!Array.isArray(paymentMethods)) return [];

        if (!availablePaymentIds.length) return paymentMethods;

        return paymentMethods.filter((payment) => {
            return availablePaymentIds.includes(String(payment._id));
        });
    }, [paymentMethods, availablePaymentIds]);

    const activePayment = useMemo(() => {
        return paymentMethods.find((item) => {
            return String(item._id) === String(selectedPaymentId);
        });
    }, [paymentMethods, selectedPaymentId]);

    const typedPrice = Number(form.price || 0);
    const nextClientPaidAmount = form.type === "client"
        ? totals.clientPaidAmount + (Number.isFinite(typedPrice) ? typedPrice : 0)
        : totals.clientPaidAmount;

    const nextOverpaymentAmount = Math.max(0, nextClientPaidAmount - totals.totalAmount);
    const willHaveOverpayment = form.type === "client" && typedPrice > 0 && nextOverpaymentAmount > 0;

    useEffect(() => {
        setSelectedPaymentId(getId(orderDetails?.paymentId));
        setPaymentOrderOnOperator(!!orderDetails?.paymentOrderOnOperator);
        setForm({
            price: "",
            description: "",
            type: "client",
        });
        setItemPictures([]);
        setFileInputKey((prev) => prev + 1);
    }, [orderDetails]);

    useEffect(() => {
        if (!orderId || !officeId) return;

        dispatch(getOfficePaymentMethods({
            officeId,
        })).then((result) => {
            const list = result?.paymentMethods || [];

            if (Array.isArray(list) && list.length) {
                setAvailablePaymentIds(list.map((item) => String(item._id)));
            }
        });

        dispatch(getOfficePaymentAccountsByOrder({
            orderId,
            officeId,
        })).then((result) => { if (result?.ok) setPaidSnapshot({ orderId, amount: result.paidAmount }); });
    }, [dispatch, orderId, officeId]);

    useEffect(() => {
        if (!showAlert) return;

        if (error) {
            setLocalError?.(error);
            return;
        }

        if (message) {
            setLocalError?.("");
        }
    }, [showAlert, error, message, setLocalError]);

    const clearErrors = () => {
        setLocalError?.("");
        dispatch(clearOfficePaymentAlert());
    };

    const setQuickPrice = (value) => {
        const safeValue = Math.max(0, Math.round(Number(value || 0)));

        clearErrors();

        setForm((prev) => ({
            ...prev,
            price: safeValue ? String(safeValue) : "",
        }));
    };

    const handleSelectOperator = () => {
        setSelectedPaymentId("");
        setPaymentOrderOnOperator(true);
        clearErrors();
    };

    const handleSelectPayment = (paymentId) => {
        setSelectedPaymentId(paymentId);
        setPaymentOrderOnOperator(false);
        clearErrors();
    };

    const handleSavePaymentMethod = () => {
        if (!orderId) {
            setLocalError?.("Не найден ID заказа");
            return;
        }

        if (!officeId) {
            setLocalError?.("Не найден ID офиса");
            return;
        }

        if (!paymentOrderOnOperator && !selectedPaymentId) {
            setLocalError?.("Выбери способ оплаты или оставь оплату оператору");
            return;
        }

        dispatch(saveOfficeOrderPaymentMethod({
            orderId,
            officeId,
            paymentId: selectedPaymentId,
            paymentOrderOnOperator,
        })).then((result) => {
            if (!result?.ok) {
                setLocalError?.(result?.error || "Не удалось сохранить способ оплаты");
                return;
            }
            emitOfficeAnalyticsRefresh({
                officeId,
                orderId,
                reason: "payment_method_saved",
                source: "OrderPaymentEditor",
            });
            clearErrors();
        });
    };

    const handleSaveOfficePaymentSettings = () => {
        setSavingSettings(true);
        clearErrors();

        setTimeout(() => {
            setSavingSettings(false);

            setLocalError?.(
                "Фронт уже умеет выбирать доступные способы оплаты. Для постоянного сохранения нужен backend endpoint /office/payment-methods."
            );
        }, 250);
    };

    const handleAddPaymentAccount = () => {
        const price = Number(form.price);

        if (!orderId) {
            setLocalError?.("Не найден ID заказа");
            return;
        }

        if (!officeId) {
            setLocalError?.("Не найден ID офиса");
            return;
        }

        if (!Number.isFinite(price) || price <= 0) {
            setLocalError?.("Укажи корректную сумму оплаты");
            return;
        }

        if (!form.description.trim()) {
            setLocalError?.("Добавь описание оплаты");
            return;
        }

        dispatch(createOfficePaymentAccount({
            orderId,
            officeId,
            price,
            type: form.type || "client",
            method: form.method,
            description: form.description.trim(),
            itemPictures,
        })).then((result) => {
            if (!result?.ok) {
                setLocalError?.(result?.error || "Не удалось добавить оплату");
                return;
            }
            setPaidSnapshot({ orderId, amount: result.paidAmount });
            emitOfficeAnalyticsRefresh({
                officeId,
                orderId,
                reason: "payment_account_created",
                source: "OrderPaymentEditor",
            });
            setForm({
                price: "",
                description: "",
                type: "client",
                method: "cash",
            });

            setItemPictures([]);
            setFileInputKey((prev) => prev + 1);
            setTab("accounts");
            clearErrors();
        });
    };

    const handleDeletePaymentAccount = (item) => {
        if (!item?._id) return;

        const ok = window.confirm(`Удалить оплату на сумму ${formatMoney(item.price)}?`);

        if (!ok) return;

        dispatch(deleteOfficePaymentAccount({
            paymentAccountId: item._id,
            orderId,
            officeId,
        })).then((result) => {
            if (!result?.ok) {
                setLocalError?.(result?.error || "Не удалось удалить оплату");
                return;
            }
            setPaidSnapshot({ orderId, amount: result.paidAmount });
            emitOfficeAnalyticsRefresh({
                officeId,
                orderId,
                reason: "payment_account_deleted",
                source: "OrderPaymentEditor",
            });
            clearErrors();
        });
    };

    return (
        <div className="order-payment-editor-AO">
            <div className="order-payment-summary-AO">
                <div className="order-payment-summary-AO__item">
                    <span>Сумма заказа</span>
                    <b>{formatMoney(totals.totalAmount)}</b>
                </div>

                <div className="order-payment-summary-AO__item order-payment-summary-AO__item--client">
                    <span>Клиент оплатил</span>
                    <b>{formatMoney(totals.clientPaidAmount)}</b>
                </div>

                <div className="order-payment-summary-AO__item">
                    <span>Осталось</span>
                    <b>{formatMoney(totals.remainingAmount)}</b>
                </div>

                <div className={[
                    "order-payment-summary-AO__item",
                    totals.overpaymentAmount > 0 ? "order-payment-summary-AO__item--overpay" : "",
                ].filter(Boolean).join(" ")}
                >
                    <span>Переплата</span>
                    <b>{formatMoney(totals.overpaymentAmount)}</b>
                </div>

                <div className="order-payment-summary-AO__item order-payment-summary-AO__item--expense">
                    <span>Расходы офиса</span>
                    <b>{formatMoney(totals.officeExpenseAmount)}</b>
                </div>

                <div className={[
                    "order-payment-summary-AO__item",
                    totals.netAmount >= 0 ? "order-payment-summary-AO__item--net" : "order-payment-summary-AO__item--negative",
                ].join(" ")}
                >
                    <span>Чистыми</span>
                    <b>{formatMoney(totals.netAmount)}</b>
                </div>

                <div className={`order-payment-summary-AO__state is-${paymentState.color}`}>
                    {paymentState.label}
                </div>

                <div className="order-payment-progress-AO">
                    <span style={{ width: `${paymentState.percent}%` }} />
                    <b>{paymentState.percent}%</b>
                </div>
            </div>

            <div className="order-payment-tabs-AO">
                <button
                    type="button"
                    className={tab === "method" ? "is-active" : ""}
                    onClick={() => setTab("method")}
                >
                    Способ оплаты
                </button>

                <button
                    type="button"
                    className={tab === "accounts" ? "is-active" : ""}
                    onClick={() => setTab("accounts")}
                >
                    Оплаты / чеки
                    <span>{paymentAccounts.length}</span>
                </button>

                <button
                    type="button"
                    className={tab === "add" ? "is-active" : ""}
                    onClick={() => setTab("add")}
                >
                    Добавить оплату
                </button>

                <button
                    type="button"
                    className={tab === "settings" ? "is-active" : ""}
                    onClick={() => setTab("settings")}
                >
                    Доступные способы
                </button>
            </div>

            {loading ? (
                <div className="ordersAO__loading">
                    Загрузка оплаты...
                </div>
            ) : (
                <>
                    {tab === "method" && (
                        <div className="order-payment-methods-AO">
                            <PaymentMethodCard
                                active={paymentOrderOnOperator || (!selectedPaymentId && !activePayment)}
                                title="Оставить оператору"
                                description="Оператор позже уточнит удобный способ оплаты."
                                operator
                                onClick={handleSelectOperator}
                                disabled={saveMethodLoading}
                            />

                            {enabledPaymentList.map((payment) => (
                                <PaymentMethodCard
                                    key={payment._id}
                                    active={!paymentOrderOnOperator && selectedPaymentId === payment._id}
                                    title={payment.title || "Способ оплаты"}
                                    description={payment.description}
                                    icon={payment.iconImg}
                                    onClick={() => handleSelectPayment(payment._id)}
                                    disabled={saveMethodLoading}
                                />
                            ))}

                            {!enabledPaymentList.length && (
                                <div className="order-payment-empty-AO">
                                    Для этого офиса не включены способы оплаты.
                                    Открой вкладку “Доступные способы”.
                                </div>
                            )}

                            <div className="order-payment-methods-AO__footer">
                                <div>
                                    <span>Выбрано:</span>
                                    <b>
                                        {paymentOrderOnOperator
                                            ? "Оставить оператору"
                                            : activePayment?.title || "Не выбрано"}
                                    </b>
                                </div>

                                <button
                                    type="button"
                                    className="btn btn--primary"
                                    onClick={handleSavePaymentMethod}
                                    disabled={saveMethodLoading}
                                >
                                    {saveMethodLoading ? "Сохраняем..." : "Сохранить способ оплаты"}
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === "accounts" && (
                        <div className="order-payment-accounts-AO">
                            <div className="order-payment-accounts-AO__summary">
                                <div>
                                    <span>Клиентские оплаты</span>
                                    <b>{formatMoney(totals.clientPaidAmount)}</b>
                                </div>

                                <div>
                                    <span>Расходы офиса</span>
                                    <b>{formatMoney(totals.officeExpenseAmount)}</b>
                                </div>

                                <div>
                                    <span>Чистыми</span>
                                    <b>{formatMoney(totals.netAmount)}</b>
                                </div>
                            </div>

                            <PaymentAccountGroup
                                title="Оплаты клиента"
                                hint={getPaymentKindHint("client")}
                                total={totals.clientPaidAmount}
                                items={clientPaymentAccounts}
                                type="client"
                                onDelete={handleDeletePaymentAccount}
                                onOpenReceipt={handleOpenReceipt}
                                deletingId={deletingId}
                                deleteLoading={deleteLoading}
                            />

                            <PaymentAccountGroup
                                title="Расходы офиса"
                                hint={getPaymentKindHint("office")}
                                total={totals.officeExpenseAmount}
                                items={officePaymentAccounts}
                                type="office"
                                onDelete={handleDeletePaymentAccount}
                                onOpenReceipt={handleOpenReceipt}
                                deletingId={deletingId}
                                deleteLoading={deleteLoading}
                            />
                        </div>
                    )}

                    {tab === "add" && (
                        <div className="order-payment-add-AO">
                            <div className="order-payment-add-AO__quick-block">
                                <div className="order-payment-add-AO__quick-title">
                                    Быстрые суммы
                                </div>

                                <div className="order-payment-quick-AO">
                                    <QuickAmountButton
                                        label="Остаток"
                                        value={totals.remainingAmount}
                                        onClick={setQuickPrice}
                                        disabled={form.type !== "client"}
                                    />

                                    <QuickAmountButton
                                        label="50%"
                                        value={totals.totalAmount * 0.5}
                                        onClick={setQuickPrice}
                                    />

                                    <QuickAmountButton
                                        label="30%"
                                        value={totals.totalAmount * 0.3}
                                        onClick={setQuickPrice}
                                    />

                                    <QuickAmountButton
                                        label="Полная сумма"
                                        value={totals.totalAmount}
                                        onClick={setQuickPrice}
                                    />
                                </div>

                                {form.type === "office" && (
                                    <div className="order-payment-note-AO order-payment-note-AO--info">
                                        Офисный платёж — это расход офиса. Он не увеличивает сумму “Клиент оплатил”.
                                    </div>
                                )}

                                {willHaveOverpayment && (
                                    <div className="order-payment-note-AO order-payment-note-AO--warning">
                                        После добавления этой оплаты будет переплата:{" "}
                                        <b>{formatMoney(nextOverpaymentAmount)}</b>
                                    </div>
                                )}
                            </div>

                            <div className="ao-rowline">
                                <div className="ao-rowline__k">
                                    Сумма
                                </div>

                                <div className="ao-rowline__v">
                                    <input
                                        className="input"
                                        type="number"
                                        value={form.price}
                                        onChange={(event) => {
                                            clearErrors();
                                            setForm((prev) => ({
                                                ...prev,
                                                price: event.target.value,
                                            }));
                                        }}
                                        placeholder="Например: 50000"
                                    />
                                </div>
                            </div>

                            <div className="ao-rowline">
                                <div className="ao-rowline__k">Способ оплаты</div>
                                <div className="ao-rowline__v">
                                    <select className="select" value={form.method} onChange={(event) => setForm((prev) => ({ ...prev, method: event.target.value }))}>
                                        <option value="cash">Наличные</option>
                                        <option value="bank_transfer">Банковский перевод</option>
                                        <option value="card">Картой вне сайта</option>
                                    </select>
                                </div>
                            </div>
                            <div className="ao-rowline">
                                <div className="ao-rowline__k">
                                    Тип платежа
                                </div>

                                <div className="ao-rowline__v">
                                    <select
                                        className="select"
                                        value={form.type}
                                        onChange={(event) => {
                                            clearErrors();
                                            setForm((prev) => ({
                                                ...prev,
                                                type: event.target.value,
                                            }));
                                        }}
                                    >
                                        <option value="client">
                                            Клиентский платёж
                                        </option>

                                        <option value="office">
                                            Офисный платёж / расход
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <div className="ao-rowline">
                                <div className="ao-rowline__k">
                                    Описание
                                </div>

                                <div className="ao-rowline__v">
                                    <textarea
                                        className="input"
                                        rows={4}
                                        value={form.description}
                                        onChange={(event) => {
                                            clearErrors();
                                            setForm((prev) => ({
                                                ...prev,
                                                description: event.target.value,
                                            }));
                                        }}
                                        placeholder="Например: предоплата, чек от клиента, расход на установку..."
                                    />
                                </div>
                            </div>

                            <div className="ao-rowline">
                                <div className="ao-rowline__k">
                                    Чеки / файлы
                                </div>

                                <div className="ao-rowline__v">
                                    <input
                                        key={fileInputKey}
                                        className="input"
                                        type="file"
                                        multiple
                                        accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                                        onChange={(event) => setItemPictures(event.target.files)}
                                    />

                                    {!!itemPictures?.length && (
                                        <div className="muted-AO mt-8">
                                            Файлов выбрано: {itemPictures.length}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="order-payment-add-AO__footer">
                                <button
                                    type="button"
                                    className="btn btn--primary"
                                    onClick={handleAddPaymentAccount}
                                    disabled={createLoading}
                                >
                                    {createLoading ? "Добавляем..." : "Добавить оплату"}
                                </button>
                            </div>
                        </div>
                    )}

                    {tab === "settings" && (
                        <OfficePaymentMethodSettings
                            paymentList={paymentMethods}
                            availablePaymentIds={availablePaymentIds}
                            setAvailablePaymentIds={setAvailablePaymentIds}
                            onSave={handleSaveOfficePaymentSettings}
                            saving={savingSettings}
                            backendReady={officePaymentSettingsReady}
                        />
                    )}
                </>
            )}

            <OfficeReceiptPreviewModal
                open={!!receiptPreview}
                paymentAccountId={receiptPreview?.paymentAccountId || ""}
                receipts={receiptPreview?.receipts || []}
                initialIndex={receiptPreview?.initialIndex || 0}
                title={receiptPreview?.title || "Чек"}
                onClose={() => setReceiptPreview(null)}
            />
        </div>
    );
}
